import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Check, AlertCircle } from 'lucide-react';

interface WaiverFormProps {
  isOpen: boolean;
  onClose: () => void;
  playerId: string;
  onComplete: () => void;
}

// Using the same enum types as defined in the database
type WaiverType = 'CLUB_LIABILITY_WAIVER' | 'COMMUNICABLE_DISEASE_WAIVER' | 'SOCIAL_MEDIA_POLICY' | 'PHOTO_RELEASE_WAIVER';
type WaiverResult = 'agreed' | 'disagreed';

interface Waiver {
  name: WaiverType;
  displayName: string;
  text: string;
  response: WaiverResult | null;
}

const waivers: Waiver[] = [
  {
    name: 'CLUB_LIABILITY_WAIVER',
    displayName: 'CLUB LIABILITY WAIVER',
    text: `I, the parent/guardian of the named player, a minor, agree that I and the player will abide by the rules and regulations of the USYSA (UNITED STATES YOUTH SOCCER ASSOCIATION), its affiliated organizations and its sponsors ("USYSA Parties"). In consideration of the player's participation in the soccer programs and activities of the USYSA Parties ("the programs"), I, for myself and the player and our respective heirs, administrator and successors, intending to be legally bound, hereby release and indemnity the USYA Parties, the owners and operators of the facilities used for the Programs, and their respective directors, officers, employees, agents and representatives from and against all claims, liabilities, damages, or caused of action arising out of or in connection with the player's participation in the Programs including, without limitation, player's transportation to/from any Program which transportation is hereby authorized.

As the parent or legal guardian of the named player, I hereby give consent for emergency medical care prescribed by a duly licensed Doctor of Medicine or Doctor of Dentistry. This care may be given under whatever conditions are necessary to preserve the life, limb or well-being of my dependent.`,
    response: null
  },
  {
    name: 'COMMUNICABLE_DISEASE_WAIVER',
    displayName: 'COMMUNICABLE DISEASE RELEASE OF LIABILITY AND ASSUMPTION OF RISK AGREEMENT',
    text: `In consideration of being allowed to participate in any way in the program, related events and activities, I the undersigned, acknowledge, appreciate, and agree that: I am aware there are risks to me of exposure to directly or indirectly arising out of, contributed to, by, or resulting from an outbreak of any and all communicable disease, including but not limited to, the virus "severe acute respiratory syndrome coronavirus 2 (SARS-CoV-2)", which is responsible for Coronavirus Disease (COVID-19) and/or any mutation or variation thereof. I, for myself and on behalf of my heirs, assigns, personal representatives and next of kin, HEREBY RELEASE, INDEMNIFY, AND HOLD HARMLESS CDO Soccer Club, ("CDO"), its officers, officials, agents and/or employees, other participants, sponsors, advertisers, and, if applicable, owners and lessors of premises used to conduct the event (RELEASEES), from any and all claims, demands, losses, and liability arising out of or related to any ILLNESS, INJURY, DISABILITY OR DEATH I may suffer, WHETHER ARISING FROM THE NEGLIGENCE OF THE RELEASEES OR OTHERWISE, to the fullest extent permitted by law.`,
    response: null
  },
  {
    name: 'SOCIAL_MEDIA_POLICY',
    displayName: 'CDO SOCCER CLUB SOCIAL MEDIA POLICY',
    text: `Purpose: To ensure that all social media* postings protect member's privacy, maintain clear boundaries, and that bullying and harassment do not occur. (Social Media*: All postings, blogs, status updates, and tweets are considered public comment. This includes, but is not limited to texts, email, Facebook, YouTube, Twitter, Instagram, or SnapChat, etc.)

What we ask of ALL CDO Soccer Club members, their family and friends is that ALL electronic communication:
-Should be positive. This includes all postings (written, photos, or videos).
-Must not be misleading, false, or likely to injure a person's reputation.
-Must not offend, intimidate, humiliate, or bully another person.
-Should respect and maintain the privacy of our members.
-Must not bring CDO Soccer Club into disrepute.

Please note: Coaches and others who work with youth MUST direct electronic communication through the child's parents.
Non-Compliance: Members who do not comply with this policy will face disciplinary action.
Please note: Under certain circumstances, cyber bullying is a criminal offense that can be reported to the police.

I, for myself and on behalf of my heirs, assigns, personal representatives and next of kin, have read and understand the Social Media Policy and will abide by it as a member of the CDO Soccer Club.`,
    response: null
  },
  {
    name: 'PHOTO_RELEASE_WAIVER',
    displayName: 'PHOTO RELEASE WAIVER',
    text: `As the child's parent/ guardian I am aware that CDO Soccer Club (CDO) and its partners and/or sponsors have the right to use the player's picture and/or likeness on Social Media Sites; "Social Media Sites" means any form of social network on the internet, including, without limitation, Website, Facebook, YouTube, Instagram, and Twitter, as well as other Club promotional purposes (flyers, handouts, brochures, etc) provided such use is related to the player's status as a participant in the program. On behalf of the participant, I understand that all depictions shall be the sole property of the Club and neither I nor the participant shall receive any compensation in connection with their use. Agreement to the Photo Release Waiver is required below. If you would like to decline permission of photo use for your child, you may email cdoscregistrar@gmail.com to express your formal written declination of the Photo Release Waiver.`,
    response: null
  }
];

export function WaiverForm({ isOpen, onClose, playerId, onComplete }: WaiverFormProps) {
  const { user } = useAuth();
  const [waiverState, setWaiverState] = useState<Waiver[]>(waivers);
  const [initials, setInitials] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const waiverContainerRef = useRef<HTMLDivElement>(null);
  
  const maxPages = waiverState.length;
  
  // Reset state when the component is opened for a different player
  useEffect(() => {
    if (isOpen) {
      // Reset waiver state to initial values
      setWaiverState(waivers.map(waiver => ({ ...waiver, response: null })));
      setInitials('');
      setError('');
      setCurrentPage(0);
    }
  }, [isOpen, playerId]);

  const handleWaiverResponse = (index: number, response: 'agreed' | 'disagreed') => {
    const newWaivers = [...waiverState];
    newWaivers[index].response = response;
    setWaiverState(newWaivers);
    
    // Clear error when selecting 'agreed'
    if (response === 'agreed') {
      setError('');
      
      // Scroll to the top of the waiver container when agreeing
      if (waiverContainerRef.current) {
        waiverContainerRef.current.scrollTop = 0;
      }
    }
    
    // Show error when selecting 'disagreed' for required waivers
    if (response === 'disagreed' && newWaivers[index].name !== 'PHOTO_RELEASE_WAIVER') {
      setError('You must agree to this waiver to continue.');
    }
  };

  const handleNext = () => {
    if (waiverState[currentPage].response === null) {
      setError('Please select "Agree" or "Don\'t Agree" before continuing.');
      return;
    }
    
    // Don't allow continuing if they disagree with a required waiver
    if (waiverState[currentPage].response === 'disagreed' && waiverState[currentPage].name !== 'PHOTO_RELEASE_WAIVER') {
      setError('You must agree to this waiver to continue.');
      return;
    }
    
    setError('');
    setCurrentPage(currentPage + 1);
  };

  const handleBack = () => {
    setError('');
    setCurrentPage(currentPage - 1);
  };

  const handleSubmit = async () => {
    // Check if all waivers have a response
    const allAnswered = waiverState.every(waiver => waiver.response !== null);
    if (!allAnswered) {
      setError('Please respond to all waivers before submitting.');
      return;
    }
    
    // Check if any required waivers have been disagreed with
    const hasDisagreedRequiredWaiver = waiverState.some(waiver => 
      waiver.response === 'disagreed' && waiver.name !== 'PHOTO_RELEASE_WAIVER'
    );
    
    if (hasDisagreedRequiredWaiver) {
      setError('You must agree to all required waivers to continue. Only the Photo Release Waiver can be declined.');
      return;
    }

    // Check for initials
    if (!initials.trim()) {
      setError('Please enter your initials to confirm your responses.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      if (!user) throw new Error('User not authenticated');

      // Save each waiver response to the database
      for (const waiver of waiverState) {
        const { error } = await supabase
          .from('waiver_log')
          .insert({
            parent_id: user.id,
            player_id: playerId,
            waiver_name: waiver.name,
            waiver_text: waiver.text,
            initials: initials,
            result_code: waiver.response
          });

        if (error) throw error;
      }

      // All waivers saved successfully
      onComplete();
      onClose();
    } catch (error: any) {
      console.error('Error saving waiver responses:', error);
      setError(error.message || 'An error occurred while saving your responses.');
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate progress percentage
  const progress = ((currentPage + 1) / maxPages) * 100;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl">Required Waivers</DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          {/* Only show progress bar and page count when viewing waivers, not on confirmation page */}
          {currentPage < maxPages && (
            <>
              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
                <div 
                  className="bg-indigo-600 h-2.5 rounded-full" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              
              {/* Waiver page count */}
              <p className="text-sm text-gray-500 mb-4">
                Page {currentPage + 1} of {maxPages}
              </p>
            </>
          )}

          {/* Current waiver */}
          {currentPage < maxPages && (
            <div className="space-y-4">
              <h3 className="font-bold text-lg">{waiverState[currentPage].displayName}</h3>
              
              <div ref={waiverContainerRef} className="bg-gray-50 p-4 rounded border border-gray-200 max-h-64 overflow-y-auto">
                <p className="whitespace-pre-line text-sm">{waiverState[currentPage].text}</p>
              </div>
              
              <div className="space-y-2">
                <p className="font-medium">Do you agree to these terms?</p>
                
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => handleWaiverResponse(currentPage, 'agreed')}
                    className={`px-4 py-2 rounded-md flex items-center ${
                      waiverState[currentPage].response === 'agreed'
                        ? 'bg-green-100 text-green-800 border border-green-500'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                    }`}
                  >
                    {waiverState[currentPage].response === 'agreed' && (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Agree
                  </button>
                  
                  {waiverState[currentPage].name === 'PHOTO_RELEASE_WAIVER' && (
                    <button
                      type="button"
                      onClick={() => handleWaiverResponse(currentPage, 'disagreed')}
                      className={`px-4 py-2 rounded-md flex items-center ${
                        waiverState[currentPage].response === 'disagreed'
                          ? 'bg-red-100 text-red-800 border border-red-500'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                      }`}
                    >
                      {waiverState[currentPage].response === 'disagreed' && (
                        <AlertCircle className="w-4 h-4 mr-2" />
                      )}
                      Don't Agree
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Final page with initials */}
          {currentPage === maxPages && (
            <div className="space-y-4">
              <h3 className="font-bold text-lg">Confirmation</h3>
              
              <div className="space-y-4">
                <p>You have responded to all waivers:</p>
                
                <ul className="space-y-2">
                  {waiverState.map((waiver, index) => (
                    <li key={index} className="flex items-start">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full mr-2 ${
                        waiver.response === 'agreed' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {waiver.response === 'agreed' ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <AlertCircle className="w-4 h-4" />
                        )}
                      </span>
                      <span className="text-sm">
                        <strong>{waiver.displayName}</strong> - {waiver.response === 'agreed' ? 'Agreed' : 'Disagreed'}
                      </span>
                    </li>
                  ))}
                </ul>
                
                <div className="pt-4">
                  <label htmlFor="initials" className="block text-sm font-medium text-gray-700 mb-1">
                    Please enter your initials to confirm:
                  </label>
                  <Input
                    id="initials"
                    value={initials}
                    onChange={(e) => setInitials(e.target.value)}
                    placeholder="Your initials"
                    className="w-48"
                    maxLength={10}
                  />
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          {currentPage > 0 ? (
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              disabled={submitting}
            >
              Back
            </Button>
          ) : (
            <div></div> // Empty div to maintain spacing
          )}

          {currentPage < maxPages ? (
            <Button 
              type="button"
              onClick={handleNext}
              disabled={waiverState[currentPage].response === null || submitting || 
                (waiverState[currentPage].response === 'disagreed' && waiverState[currentPage].name !== 'PHOTO_RELEASE_WAIVER')}
            >
              Continue
            </Button>
          ) : (
            <Button 
              type="button"
              onClick={handleSubmit}
              disabled={!initials.trim() || submitting}
            >
              {submitting ? 'Submitting...' : 'Complete registration'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WaiverForm;
